import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

import Const from '../Constants';

import {
  rankBetween,
  rankCompare,
  isEqual,
  getTodayFSDate,
  sameDay
} from '../Utils';

// Maps a view type (Meetings / OneOnOnes) to the API type field (meeting / 1on1).
function apiType(type) {
  return type === Const.sMeetings ? Const.sMeeting : Const.s1on1;
}

// Name of the checkable array on meeting.data for a given checkable type.
function arrayName(type) {
  return type === Const.sAgenda ? 'agenda' : 'actionItems';
}

// If there is no note with today's date, prepend an empty one so the details
// view always opens on a fresh note. Ported from Meetings.js.
function addTodaysMeetingNotes(meeting) {
  const today = getTodayFSDate();
  const emptyTodaysMeetingNote = {
    meetingId: meeting.id,
    date: today,
    body: '',
    clientId: uuidv4()
  };

  // Give every note a stable client-side identity so it can be matched without
  // relying on its (possibly missing) server id or its array position.
  meeting.data.meetingNotes.forEach((note) => {
    if (!note.clientId) {
      note.clientId = uuidv4();
    }
  });

  if (meeting.data.meetingNotes.length === 0) {
    meeting.data.meetingNotes.push(emptyTodaysMeetingNote);
  } else if (meeting.data.meetingNotes[0].date !== today) {
    if (!sameDay(today, meeting.data.meetingNotes[0].date)) {
      meeting.data.meetingNotes.unshift(emptyTodaysMeetingNote);
    }
  }

  return meeting;
}

export const useMeetingsStore = create((set, get) => ({

  // List of meetings for the active type (each may lazily gain a `.data`).
  meetings: [],

  // The selected meeting, including its loaded `.data` (agenda/actionItems/notes).
  currentMeeting: null,

  // Active view type (Const.sMeetings | Const.s1on1s), used to reload on switch.
  currentType: null,

  setMeetings: (meetings) => set({ meetings }),

  setCurrentMeeting: (currentMeeting) => set({ currentMeeting }),

  loadMeetings: async (auth, type) => {
    try {
      const queryParams = new URLSearchParams({ type: apiType(type) });
      const idToken = await auth.currentUser.getIdToken();
      const theURL = `${process.env.REACT_APP_BASE_URL}${Const.sMeetingsPath}?${queryParams}`;

      const response = await fetch(theURL, {
        headers: new Headers({ Authorization: idToken })
      });
      const data = await response.json();

      set({ meetings: data, currentMeeting: null, currentType: type });
    } catch (error) {
      console.error('Error loading meetings:', error.message);
    }
  },

  loadMeetingDetails: async (auth, id) => {
    try {
      const queryParams = new URLSearchParams({ id });
      const idToken = await auth.currentUser.getIdToken();
      const theURL = `${process.env.REACT_APP_BASE_URL}${Const.sMeetingsPath}?${queryParams}`;

      const response = await fetch(theURL, {
        headers: new Headers({ Authorization: idToken })
      });

      let meeting = await response.json();
      meeting = addTodaysMeetingNotes(meeting);

      // Backfill ranks for any legacy checkables that predate ordering, then
      // sort each list by rank. Persistence is fire-and-forget.
      get()._backfillRanks(auth, meeting.data.agenda);
      get()._backfillRanks(auth, meeting.data.actionItems);
      meeting.data.agenda.sort(rankCompare);
      meeting.data.actionItems.sort(rankCompare);

      const { meetings, currentMeeting } = get();

      const updatedMeetings = meetings.map((m) =>
        m.id === id ? { ...m, data: meeting.data } : m
      );
      set({ meetings: updatedMeetings });

      if (currentMeeting && currentMeeting.id === meeting.id) {
        if (!currentMeeting.data || !isEqual(currentMeeting.data, meeting.data)) {
          set({ currentMeeting: { ...currentMeeting, data: meeting.data } });
        }
      }
    } catch (error) {
      console.error('Error loading meeting details:', error.message);
    }
  },

  // Assigns sequential ranks to any items in `list` (in place) that lack one,
  // persisting each via PATCH. Items are assumed to already be in display order.
  _backfillRanks: (auth, list) => {
    if (!list || list.every((item) => item.rank)) return;

    let prev = null;
    list.forEach((item) => {
      if (item.rank) {
        prev = item.rank;
        return;
      }
      const rank = rankBetween(prev, null);
      item.rank = rank;
      prev = rank;
      get()._patchCheckable(auth, { id: item.id, rank }).catch((e) =>
        console.error('Failed to backfill checkable rank:', e)
      );
    });
  },

  // Low-level PATCH to /meetings/checkables. Caller updates local state.
  _patchCheckable: async (auth, { id, title = null, status = null, rank = null }) => {
    const idToken = await auth.currentUser.getIdToken();
    const theURL = process.env.REACT_APP_BASE_URL + Const.sMeetingsCheckablesPath;
    const response = await fetch(theURL, {
      method: Const.HttpPatch,
      headers: new Headers({
        Authorization: idToken,
        Accept: Const.AppJson,
        [Const.ContentType]: Const.AppJson
      }),
      body: JSON.stringify({ id, title, status, rank })
    });
    if (!response.ok) {
      throw new Error('Failed to update meeting checkable');
    }
  },

  // Applies `mutate(array)` (must return a new array) to the given checkable list
  // on both currentMeeting and its entry in the meetings list.
  _applyToCheckables: (type, mutate) => {
    const { meetings, currentMeeting } = get();
    if (!currentMeeting || !currentMeeting.data) return;

    const key = arrayName(type);
    const newCurrent = JSON.parse(JSON.stringify(currentMeeting));
    newCurrent.data[key] = mutate(newCurrent.data[key] || []);

    const newMeetings = meetings.map((m) =>
      m.id === currentMeeting.id ? { ...m, data: newCurrent.data } : m
    );

    set({ currentMeeting: newCurrent, meetings: newMeetings });
  },

  addCheckable: async (auth, type) => {
    try {
      const { currentMeeting } = get();
      if (!currentMeeting) return;

      const list = currentMeeting.data?.[arrayName(type)] || [];
      const last = list[list.length - 1];
      const rank = rankBetween(last?.rank ?? null, null);

      const idToken = await auth.currentUser.getIdToken();
      const theURL = process.env.REACT_APP_BASE_URL + Const.sMeetingsCheckablesPath;
      const response = await fetch(theURL, {
        method: Const.HttpPut,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson
        }),
        body: JSON.stringify({ type, parentId: currentMeeting.id, rank })
      });
      if (!response.ok) {
        throw new Error('Failed to add meeting checkable');
      }

      const checkable = await response.json();
      get()._applyToCheckables(type, (arr) => [...arr, checkable].sort(rankCompare));
    } catch (error) {
      console.error('Error adding checkable:', error.message);
    }
  },

  updateCheckable: async (auth, checkable, title, status) => {
    try {
      await get()._patchCheckable(auth, { id: checkable.id, title, status });

      get()._applyToCheckables(checkable.type, (arr) =>
        arr.map((item) => {
          if (item.id !== checkable.id) return item;
          if (title !== null) return { ...item, title };
          if (status !== null) return { ...item, status };
          return item;
        })
      );
    } catch (error) {
      console.error('Error updating checkable:', error.message);
    }
  },

  deleteCheckable: async (auth, checkable) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const theURL = process.env.REACT_APP_BASE_URL + Const.sMeetingsCheckablesPath;
      const response = await fetch(theURL, {
        method: Const.HttpDelete,
        headers: new Headers({
          Authorization: idToken,
          Accept: Const.AppJson,
          [Const.ContentType]: Const.AppJson
        }),
        body: JSON.stringify({ id: checkable.id })
      });
      if (!response.ok) {
        throw new Error('Failed to delete meeting checkable');
      }

      get()._applyToCheckables(checkable.type, (arr) =>
        arr.filter((item) => item.id !== checkable.id)
      );
    } catch (error) {
      console.error('Error deleting checkable:', error.message);
    }
  },

  // Drop a checkable into a new position within its own list.
  // beforeId / afterId may be null for top-of-list / bottom-of-list inserts.
  moveCheckable: async (auth, { type, itemId, beforeId, afterId }) => {
    try {
      const { currentMeeting } = get();
      if (!currentMeeting || !currentMeeting.data) return;

      const list = currentMeeting.data[arrayName(type)] || [];
      const item = list.find((i) => i.id === itemId);
      if (!item) return;

      const beforeRank = beforeId ? list.find((i) => i.id === beforeId)?.rank : null;
      const afterRank = afterId ? list.find((i) => i.id === afterId)?.rank : null;
      const newRank = rankBetween(beforeRank ?? null, afterRank ?? null);

      get()._applyToCheckables(type, (arr) =>
        arr
          .map((i) => (i.id === itemId ? { ...i, rank: newRank } : i))
          .sort(rankCompare)
      );

      try {
        await get()._patchCheckable(auth, { id: itemId, rank: newRank });
      } catch (e) {
        console.error('moveCheckable backend update failed, reloading:', e);
        await get().loadMeetingDetails(auth, currentMeeting.id);
      }
    } catch (error) {
      console.error('Error moving checkable:', error.message);
    }
  }

}));
